import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Badge } from "@/components/ui/badge";
import { Users, ChevronDown, ChevronUp } from "lucide-react";
import { prospectStatusClass, stepStatusClass, stepCardClass } from "./campaignModel";

const StepTimeline = ({ steps }) => (
  <div className="ml-11 pl-4 border-l border-white/10 space-y-3 py-3">
    {steps.map((step) => (
      <div key={step.stepNum} className="relative">
        <div className="absolute -left-[21px] top-1.5 w-2.5 h-2.5 rounded-full border-2 border-white/20 bg-[#111]" />
        <div className={`p-3 rounded-lg ${stepCardClass(step.status)}`}>
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs text-gray-400">Step {step.stepNum} — {step.dayLabel || `Day ${step.dayOffset || 0}`}</span>
            <Badge className={`text-xs border ${stepStatusClass(step.status)}`}>{step.status}</Badge>
          </div>
          <p className="text-sm text-gray-300">Subject: {step.subject}</p>
          <p className="text-xs text-gray-500 mt-1 line-clamp-2">{step.body}</p>
          {step.sentAt && <p className="text-xs text-green-400/60 mt-1">Sent {new Date(step.sentAt).toLocaleString()}</p>}
        </div>
      </div>
    ))}
  </div>
);

const ProspectRow = ({ prospect: p, idx, steps, expanded, onToggle }) => (
  <div data-testid={`prospect-row-${idx}`}>
    <div className="glass-card p-3 flex items-center justify-between gap-3 cursor-pointer hover:border-white/20 transition-colors" onClick={onToggle}>
      <div className="flex items-center gap-3 min-w-0">
        <div className="w-8 h-8 rounded-full bg-ism-teal/20 flex items-center justify-center text-xs text-ism-teal font-medium flex-shrink-0">
          {(p.contactName || "?")[0]}
        </div>
        <div className="min-w-0">
          <p className="text-sm text-white truncate">{p.contactName}</p>
          <p className="text-xs text-gray-500 truncate">{p.contactTitle ? `${p.contactTitle} at ` : ""}{p.companyName}</p>
        </div>
      </div>
      <div className="flex items-center gap-3 flex-shrink-0">
        {p.contactEmail && <span className="text-xs text-gray-500 font-mono hidden md:inline">{p.contactEmail}</span>}
        {steps.length > 0 && <span className="text-xs text-gray-600">{steps.filter((s) => s.status === "sent").length}/{steps.length} sent</span>}
        <Badge className={`text-xs border ${prospectStatusClass(p.status)}`}>{p.status}</Badge>
        {expanded ? <ChevronUp className="h-4 w-4 text-gray-500" /> : <ChevronDown className="h-4 w-4 text-gray-500" />}
      </div>
    </div>
    <AnimatePresence>
      {expanded && steps.length > 0 && (
        <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
          <StepTimeline steps={steps} />
        </motion.div>
      )}
    </AnimatePresence>
  </div>
);

export const ProspectList = ({ prospects, sequences }) => {
  const [expandedId, setExpandedId] = useState(null);
  return (
    <div data-testid="prospect-list">
      <h2 className="text-lg text-white font-medium mb-3 flex items-center gap-2">
        <Users className="h-5 w-5 text-gray-400" />
        Prospects ({prospects.length})
      </h2>
      <div className="grid gap-2">
        {prospects.map((p, idx) => {
          const seq = sequences.find((s) => s.prospectId === p.prospectId);
          return (
            <ProspectRow
              key={p.prospectId}
              prospect={p}
              idx={idx}
              steps={seq?.steps || []}
              expanded={expandedId === p.prospectId}
              onToggle={() => setExpandedId(expandedId === p.prospectId ? null : p.prospectId)}
            />
          );
        })}
      </div>
    </div>
  );
};
